import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, ShieldOff, ArrowLeft, TrendingUp, ShoppingCart, CalendarCheck, Wallet, BarChart3, RefreshCw } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { format, parse } from 'date-fns';
import { useAuth } from '@/lib/AuthContext';

const EXPERIENCE_LABELS = {
  adult_sim_racing: 'Adult Sim',
  child_sim_racing: 'Child Sim',
  young_racer: 'Young Racer',
};
const EXPERIENCE_COLORS = { adult_sim_racing: '#e11d2a', child_sim_racing: '#f59e0b', young_racer: '#10b981' };
const GA_PROPERTY_KEY = 'mr_ga4_property_id';

const fmtMonth = (m) => {
  try { return format(parse(m, 'yyyy-MM', new Date()), 'MMM'); } catch { return m; }
};
const gbp = (n) => `£${(n || 0).toFixed(2)}`;

const chartTooltipStyle = {
  contentStyle: { background: '#111115', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 0, fontSize: 12 },
  labelStyle: { color: 'rgba(255,255,255,0.5)' },
  itemStyle: { color: '#fff' },
};

function KpiCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#111115] border border-white/5 p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-primary" />
        <p className="text-white/30 text-[10px] font-bold tracking-widest">{label}</p>
      </div>
      <p className="text-white font-black text-2xl">{value}</p>
    </div>
  );
}

export default function AdminAnalytics() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-analytics'],
    queryFn: async () => {
      const res = await base44.functions.invoke('adminAnalytics', {});
      return res.data;
    },
    enabled: currentUser?.role === 'admin',
  });

  const [propertyId, setPropertyId] = useState(() => localStorage.getItem(GA_PROPERTY_KEY) || '');
  const [gaData, setGaData] = useState(null);
  const [gaLoading, setGaLoading] = useState(false);
  const [gaError, setGaError] = useState('');

  const fetchGa = async () => {
    if (!propertyId) return;
    localStorage.setItem(GA_PROPERTY_KEY, propertyId);
    setGaLoading(true); setGaError('');
    try {
      const res = await base44.functions.invoke('adminGaMetrics', { propertyId });
      setGaData(res.data);
    } catch (err) {
      const d = err?.response?.data || {};
      setGaError(d.notConnected
        ? 'Google Analytics not connected — connect it from the build chat to enable visitor stats.'
        : (d.error || 'Failed to fetch GA data.'));
      setGaData(null);
    } finally {
      setGaLoading(false);
    }
  };

  useEffect(() => {
    if (propertyId && currentUser?.role === 'admin') fetchGa();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <p className="text-white/20 text-xs mt-2">You must be an admin to view this page.</p>
        </div>
      </div>
    );
  }

  const k = data?.kpis;
  const pieData = data ? Object.entries(data.experienceBreakdown).map(([key, value]) => ({ name: EXPERIENCE_LABELS[key] || key, value })) : [];

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">SYSTEM &amp; INTEGRATIONS</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-primary" /> ANALYTICS
          </h1>
          <button onClick={() => refetch()} className="mt-4 inline-flex items-center gap-2 border border-white/10 hover:border-primary/50 text-white/50 hover:text-white text-xs font-bold tracking-wider px-4 py-2.5 transition-all">
            <RefreshCw className="w-4 h-4" /> REFRESH
          </button>
        </div>
      </section>

      {/* KPIs */}
      <section className="px-6 lg:px-10 pt-10 max-w-6xl mx-auto">
        {isLoading || !k ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard icon={Wallet} label="TOTAL REVENUE" value={gbp(k.totalRevenue)} />
              <KpiCard icon={ShoppingCart} label="ORDERS" value={k.totalOrders} />
              <KpiCard icon={CalendarCheck} label="BOOKINGS" value={k.totalBookings} />
              <KpiCard icon={TrendingUp} label="AVG ORDER" value={gbp(k.avgOrderValue)} />
            </div>

            {/* Revenue trend */}
            <div className="mt-8 bg-[#111115] border border-white/5 p-6">
              <h2 className="text-white font-bold text-sm tracking-widest mb-5">REVENUE (LAST 6 MONTHS)</h2>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.monthlyRevenue} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#e11d2a" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#e11d2a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" tickFormatter={fmtMonth} stroke="rgba(255,255,255,0.2)" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="rgba(255,255,255,0.2)" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `£${v}`} />
                  <Tooltip {...chartTooltipStyle} formatter={(v) => gbp(v)} />
                  <Area type="monotone" dataKey="revenue" stroke="#e11d2a" strokeWidth={2} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Bookings trend + experience breakdown */}
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-[#111115] border border-white/5 p-6">
                <h2 className="text-white font-bold text-sm tracking-widest mb-5">BOOKINGS BY MONTH</h2>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.monthlyBookings} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <XAxis dataKey="month" tickFormatter={fmtMonth} stroke="rgba(255,255,255,0.2)" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="rgba(255,255,255,0.2)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip {...chartTooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="adult_sim_racing" name="Adult Sim" stackId="a" fill={EXPERIENCE_COLORS.adult_sim_racing} />
                    <Bar dataKey="child_sim_racing" name="Child Sim" stackId="a" fill={EXPERIENCE_COLORS.child_sim_racing} />
                    <Bar dataKey="young_racer" name="Young Racer" stackId="a" fill={EXPERIENCE_COLORS.young_racer} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-[#111115] border border-white/5 p-6">
                <h2 className="text-white font-bold text-sm tracking-widest mb-5">EXPERIENCE BREAKDOWN</h2>
                {pieData.every(p => p.value === 0) ? (
                  <p className="text-white/30 text-sm py-20 text-center">No bookings yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name}: ${e.value}`} labelLine={false}>
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={EXPERIENCE_COLORS[Object.keys(EXPERIENCE_LABELS)[i]] || '#666'} />
                        ))}
                      </Pie>
                      <Tooltip {...chartTooltipStyle} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Top products */}
            <div className="mt-8 bg-[#111115] border border-white/5 p-6">
              <h2 className="text-white font-bold text-sm tracking-widest mb-5">TOP PRODUCTS BY REVENUE</h2>
              {data.topProducts.length === 0 ? (
                <p className="text-white/30 text-sm py-10 text-center">No sales recorded yet.</p>
              ) : (
                <div className="space-y-3">
                  {data.topProducts.map((p, i) => (
                    <div key={p.name} className="flex items-center gap-4">
                      <span className="text-primary font-black text-sm w-6">{i + 1}</span>
                      <p className="text-white text-sm flex-1 truncate">{p.name}</p>
                      <span className="text-white/30 text-xs">{p.count} order{p.count !== 1 ? 's' : ''}</span>
                      <p className="text-white font-bold text-sm w-24 text-right">{gbp(p.revenue)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {/* GA Visitors */}
      <section className="px-6 lg:px-10 pt-8 pb-16 max-w-6xl mx-auto">
        <div className="bg-[#111115] border border-white/5 p-6">
          <h2 className="text-white font-bold text-sm tracking-widest mb-1">SITE VISITORS (GOOGLE ANALYTICS)</h2>
          <p className="text-white/30 text-xs mb-5">Enter your GA4 property ID (a number, found in GA4 Admin → Property settings). Connect your GA account from the build chat to enable this section.</p>

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <input
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              placeholder="e.g. 123456789"
              className="flex-1 bg-[#1a1a1f] border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-primary/50"
            />
            <button onClick={fetchGa} disabled={gaLoading || !propertyId}
              className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wide px-5 py-2.5 transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
              {gaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} LOAD
            </button>
          </div>

          {gaError && (
            <p className="text-white/40 text-sm bg-white/[0.02] border border-white/5 px-4 py-3">{gaError}</p>
          )}

          {gaData && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <KpiCard icon={TrendingUp} label="USERS" value={gaData.totals.users.toLocaleString()} />
                <KpiCard icon={BarChart3} label="SESSIONS" value={gaData.totals.sessions.toLocaleString()} />
                <KpiCard icon={TrendingUp} label="PAGEVIEWS" value={gaData.totals.pageviews.toLocaleString()} />
                <KpiCard icon={TrendingUp} label="NEW USERS" value={gaData.totals.newUsers.toLocaleString()} />
              </div>

              <h3 className="text-white/40 text-[10px] font-bold tracking-widest mb-3">TOP PAGES</h3>
              <div className="space-y-2">
                {gaData.topPages.map((p, i) => (
                  <div key={i} className="flex items-center gap-4 py-2 border-b border-white/5 last:border-0">
                    <p className="text-white text-sm flex-1 truncate font-mono">{p.path}</p>
                    <span className="text-white/30 text-xs">{p.users} users</span>
                    <p className="text-white font-bold text-sm w-20 text-right">{p.views} views</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}